import React, { useEffect, useState } from 'react';
import { Box, Breadcrumbs, Button, Link, Skeleton, Typography } from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FormControlledTextField } from '../../common/Form/FormTextField';
import { FormNumberField } from '../../common/Form/FormNumberField';
import ResourceSelector from '../../common/ResourceSelector';
import { FieldRequiredMark } from '../../common/Form/FieldRequiredMark';
import Loader from '../../common/Loader';
import MssAlert from '../../common/MssAlert';
import MssApiError from '../../common/MssApiError';
import { MAX_QUIZ_QUESTIONS } from '../../common/constants';
import { getQuestionCountError, getQuestionTotal } from '../helpers';
import { getData, postData } from '../../../DataAccessLayer';
import { quizzes, modules as modulesService } from '../../../DataAccessLayer/services';

const DEFAULT_SPLIT = { trueFalseCount: 5, multipleChoiceCount: 5 };

function QuizGenerator() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const courseId = useSelector((state) => state.course.courseId);

  const [modulesList, setModulesList] = useState([]);
  const [isLoadingModules, setIsLoadingModules] = useState(true);
  const [isModulesError, setIsModulesError] = useState(false);
  const [selectedResources, setSelectedResources] = useState([]);
  /*
   * The two counts are independent: between them they set both how long the
   * quiz is and which question types it asks for.
   */
  const [split, setSplit] = useState(DEFAULT_SPLIT);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { control, handleSubmit } = useForm({
    mode: 'onChange',
    defaultValues: { title: '' },
  });

  const loadModules = async () => {
    setIsLoadingModules(true);
    setIsModulesError(false);
    try {
      const data = await getData(modulesService(courseId));
      setModulesList(data ?? []);
    } catch (err) {
      console.error(err);
      setIsModulesError(true);
    } finally {
      setIsLoadingModules(false);
    }
  };

  useEffect(() => {
    loadModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const total = getQuestionTotal(split);
  const hasEveryCount = split.trueFalseCount !== '' && split.multipleChoiceCount !== '';
  // An empty box is not an error yet — the Generate button stays disabled instead.
  const countError = hasEveryCount
    ? getQuestionCountError({ total, maxQuestions: MAX_QUIZ_QUESTIONS })
    : '';

  const handleCountChange = (field) => (e) =>
    setSplit((prev) => ({
      ...prev,
      [field]: e.target.value === '' ? '' : Number(e.target.value),
    }));

  // The title is optional, so what is left is the resources and the counts.
  const isValid = selectedResources.length > 0 && hasEveryCount && !countError;

  const onSubmit = async ({ title }) => {
    setIsSubmitting(true);
    try {
      const selectedResourceNames = modulesList
        .filter((module) => selectedResources.includes(module.id))
        .map((module) => module.name);

      const newQuiz = await postData(quizzes(), {
        // Left blank, the backend names the quiz after the resources below.
        title: title.trim(),
        resources: selectedResourceNames,
        trueFalseCount: Number(split.trueFalseCount || 0),
        multipleChoiceCount: Number(split.multipleChoiceCount || 0),
        // LTI requests carry a signed courseId the backend prefers; dev-portal
        // requests have no other trusted source for it.
        courseId,
      });
      navigate(`${pathname}/${newQuiz.id}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate practice quiz. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className="mss__centeredScreen mss__generator" id="quizGenerator">
      <Loader id="quizGenerator__loader_submit" isOpen={isSubmitting} />
      <Breadcrumbs className="mss__breadcrumbs" aria-label="breadcrumb">
        <Link component={RouterLink} to="..">
          myStudySidekick
        </Link>
        <Typography>Practice Quiz Generator</Typography>
      </Breadcrumbs>

      {isModulesError && (
        <MssAlert id="quizGenerator__alert" severity="error" type="statusAlert">
          <MssApiError
            id="quizGenerator"
            widgetName="Practice Quiz Generator"
            isPage
            reload={loadModules}
          />
        </MssAlert>
      )}

      {!isModulesError && (
        <>
          <Box className="generatorHeading">
            <Typography variant="h2" component="h1">
              Generate Practice Quiz
            </Typography>
          </Box>

          <Box className="mss__box generatorForm" id="quizGenerator__box_form">
            <Box className="formSection">
              <Typography className="sectionLabel sectionLabel--accent">
                Practice Quiz Title
              </Typography>
              {isLoadingModules ? (
                <Skeleton variant="rectangular" height={56} />
              ) : (
                <FormControlledTextField
                  name="title"
                  control={control}
                  ariaLabel="Practice quiz title"
                  placeholder="Enter practice quiz title"
                  className="titleField"
                />
              )}
            </Box>

            <Box className="formSection">
              {isLoadingModules ? (
                <Skeleton variant="rectangular" height={160} />
              ) : (
                <ResourceSelector
                  id="quizGenerator"
                  modules={modulesList}
                  selected={selectedResources}
                  onChange={setSelectedResources}
                  helperText="Select resources from which the quiz will be generated."
                  required
                />
              )}
            </Box>

            <Box className="formSection">
              <Typography className="sectionLabel sectionLabel--accent">
                Quiz Settings <FieldRequiredMark label="Number of questions" />
              </Typography>
              <Typography className="sectionHelper">
                Ask for as many of each question type as you like, up to {MAX_QUIZ_QUESTIONS}{' '}
                questions in total.
              </Typography>

              {isLoadingModules ? (
                <Skeleton variant="rectangular" height={56} />
              ) : (
                <Box className="questionSplit" id="quizGenerator__box_questionSplit">
                  <Box className="splitField">
                    <Typography className="settingLabel" component="label" htmlFor="trueFalseCount">
                      Number of True/False
                    </Typography>
                    <FormNumberField
                      id="trueFalseCount"
                      value={split.trueFalseCount}
                      onChange={handleCountChange('trueFalseCount')}
                      ariaLabel="Number of true/false questions"
                      htmlInputProps={{ min: 0, max: MAX_QUIZ_QUESTIONS }}
                      error={!!countError}
                      fullWidth
                    />
                  </Box>

                  <Box className="splitField">
                    <Typography
                      className="settingLabel"
                      component="label"
                      htmlFor="multipleChoiceCount"
                    >
                      Number of Multiple Choice
                    </Typography>
                    <FormNumberField
                      id="multipleChoiceCount"
                      value={split.multipleChoiceCount}
                      onChange={handleCountChange('multipleChoiceCount')}
                      ariaLabel="Number of multiple choice questions"
                      htmlInputProps={{ min: 0, max: MAX_QUIZ_QUESTIONS }}
                      error={!!countError}
                      fullWidth
                    />
                  </Box>
                </Box>
              )}

              {countError && (
                <Typography className="splitError" id="quizGenerator__text_countError">
                  {countError}
                </Typography>
              )}

              <Box className="mss__formActions">
                <Button
                  variant="contained"
                  className="mss__button primary"
                  id="quizGenerator__button_generate"
                  disabled={!isValid || isSubmitting || isLoadingModules}
                  onClick={handleSubmit(onSubmit)}
                >
                  Generate Practice Quiz
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

export default QuizGenerator;
